// Partners — Affiliate & Partner Program
// Psychological job: Convert solution-aware audience into applicants

import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, DollarSign, Percent, Users, Globe,
  CheckCircle2, Gift, Star, TrendingUp,
  MessageSquare, Wallet,
} from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const BENEFITS = [
  { icon: Percent, title: "Recurring 20% commission", desc: "Every merchant you refer pays you 20% of their subscription fee for the first 12 months. A single referral at the Establishment tier earns you $429.60/year. Five referrals? That is $2,148/year in passive income." },
  { icon: Wallet, title: "Paid out monthly with no minimum threshold", desc: "Commissions hit your account every 30 days. Whether you earned $50 or $5,000, it is yours. No hidden holdbacks, no cumulative thresholds to chase." },
  { icon: Globe, title: "Promote a product merchants actually want", desc: "Restaurants, retailers, and service providers are switching to ordrs every week. They need a modern storefront, an AI phone host, and a backend that does not charge per-transaction percentages." },
  { icon: Gift, title: "Your own referral dashboard and unique link", desc: "Track clicks, signups, and commissions in real time. No spreadsheets. Every referral is tagged, tracked, and credited automatically from the moment you join." },
  { icon: TrendingUp, title: "Priority access to new features and beta programs", desc: "Partners get early access to every product launch including appointment booking, SMS marketing, loyalty programs, and delivery integration." },
  { icon: Users, title: "Dedicated partner support and swag kit", desc: "A real human answers your questions. You get marketing assets, email templates, and social copy you can adapt in minutes." },
];

const TESTIMONIALS = [
  { quote: "I was skeptical at first - every SaaS company claims their affiliate program is the best. But the tracking actually works, payouts are on time, and my audience loves that ordrs does not eat their margins.", author: "Jordan M.", role: "Restaurant consultant, 14 merchants referred" },
  { quote: "I promote about 6 tools to my audience. ordrs is the only one where I feel like I am doing my followers a favor. The AI phone feature alone closes the deal every time.", author: "Priya K.", role: "Food industry content creator, 47K subscribers" },
];

function PartnerForm() {
  const [form, setForm] = useState({ name: "", email: "", website: "", audience: "", promotion_idea: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setSending(true);
    try {
      await api.post("/partner-signup", form);
      setSent(true);
      toast.success("You are in. We will send partner details within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Email partner@ordrs.ai and we will set you up manually.");
    } finally { setSending(false); }
  };
  return (
    <Card className="p-8 lg:p-10">
      {sent ? (
        <div className="py-12 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }}>
            <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
          </motion.div>
          <h3 className="text-2xl font-bold mb-2">You are on the list.</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            We will send your unique referral link, dashboard access, and marketing kit within 24 hours. Watch for an email from <strong>partner@ordrs.ai</strong>.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="partner-name">Full name *</Label>
              <Input id="partner-name" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="partner-email">Email *</Label>
              <Input id="partner-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-website">Website / social profile</Label>
            <Input id="partner-website" value={form.website} onChange={set("website")} placeholder="instagram.com/yourhandle or yourwebsite.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-audience">Who is your audience?</Label>
            <Textarea id="partner-audience" value={form.audience} onChange={set("audience")} placeholder="Restaurant owners, service providers, retail shops, food bloggers..." rows={2} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-idea">How would you promote ordrs?</Label>
            <Textarea id="partner-idea" value={form.promotion_idea} onChange={set("promotion_idea")} placeholder="Social content, email list, one-on-one consulting, webinars..." rows={2} />
          </div>
          <Button type="submit" size="lg" className="h-12 px-8 text-base font-bold group w-full sm:w-auto" disabled={sending}>
            {sending ? "Submitting..." : <>Become a partner <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" /></>}
          </Button>
          <p className="text-xs text-muted-foreground pt-1">No commitment. We will review and send details within 24 hours.</p>
        </form>
      )}
    </Card>
  );
}

export default function Partners() {
  return (
    <div>
      <RevealNav />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" alt="" className="w-full h-full object-cover opacity-[0.07]" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="ds-container pt-32 pb-20 lg:pt-40 lg:pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="overline mb-6">partner program · apply now</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-4xl">
              Turn your influence into{" "}
              <span className="font-script text-primary text-6xl sm:text-7xl lg:text-[8rem] font-normal leading-[0.75] block mt-3">recurring income.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
              Every restaurant, retailer, or service business you send our way earns you 20% of their subscription for 12 months. No minimums. Real-time tracking. Monthly payouts.
            </p>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">Avg partner earns <strong className="text-foreground">$1,247/yr</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground"><strong className="text-foreground">140+</strong> active partners</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground"><strong className="text-foreground">4.8/5</strong> partner satisfaction</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="ds-container pb-20 lg:pb-28">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.p className="overline mb-3" {...fade}>how it works</motion.p>
          <motion.h2 className="text-3xl lg:text-4xl mb-4" {...fade}>
            Three steps to <span className="font-script text-primary text-4xl lg:text-5xl font-normal">passive revenue.</span>
          </motion.h2>
          <motion.p className="text-muted-foreground" {...fade}>No complicated tiers. You bring the audience; we handle everything else.</motion.p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { step: "01", title: "Apply and get your link", desc: "Fill out the form. Within 24 hours you will receive a unique referral link, dashboard access, and your marketing kit.", icon: MessageSquare },
            { step: "02", title: "Share your way", desc: "Write a review, record a video, send an email, or mention us in a consultation. Every signup is tracked automatically from your link.", icon: Globe },
            { step: "03", title: "Get paid every month", desc: "Commissions hit your account monthly. No minimum, no chasing people down.", icon: Wallet },
          ].map((item, i) => (
            <motion.div key={item.step} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}>
              <Card className="p-6 h-full text-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold text-sm flex items-center justify-center mx-auto mb-4">{item.step}</div>
                <item.icon className="w-5 h-5 text-primary mx-auto mb-3" />
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <div className="ds-container"><hr className="border-border" /></div>

      <section className="ds-container py-20 lg:py-28">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.p className="overline mb-3" {...fade}>why partners choose ordrs</motion.p>
          <motion.h2 className="text-3xl lg:text-4xl mb-4" {...fade}>
            Built for people who <span className="font-script text-primary text-4xl lg:text-5xl font-normal">actually refer.</span>
          </motion.h2>
          <motion.p className="text-muted-foreground" {...fade}>Most affiliate programs are an afterthought. Ours is built around one question: what would make you want to tell everyone you know?</motion.p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
              <Card className="p-6 h-full group hover:border-primary/30 transition-colors">
                <b.icon className="w-5 h-5 text-primary mb-3" />
                <h3 className="font-bold text-base mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="ds-container pb-20">
        <div className="max-w-3xl mx-auto mb-12 text-center">
          <motion.p className="overline mb-3" {...fade}>from partners</motion.p>
          <motion.h2 className="text-3xl lg:text-4xl" {...fade}><span className="font-script text-primary text-4xl lg:text-5xl font-normal">Real people,</span> real results.</motion.h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.author} initial={{ opacity: 0, x: i === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
              <Card className="p-8 h-full flex flex-col">
                <Star className="w-4 h-4 text-primary mb-3" />
                <blockquote className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">&ldquo;{t.quote}&rdquo;</blockquote>
                <div className="border-t border-border pt-3">
                  <p className="text-sm font-bold">{t.author}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-border">
        <div className="ds-container py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <motion.div className="lg:col-span-5 lg:sticky lg:top-32" {...fade}>
              <p className="overline mb-4">apply now</p>
              <h2 className="text-3xl lg:text-4xl mb-4">
                Ready to turn your network into <span className="font-script text-primary text-4xl lg:text-5xl font-normal">revenue?</span>
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Fill out the form and we will send your partner kit within 24 hours.
              </p>
              <ul className="space-y-3 text-sm">
                {["20% recurring commission - 12 months per referral", "Monthly payouts, no minimum", "Real-time tracking dashboard", "Marketing kit and dedicated support", "Early access to new features"].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div className="lg:col-span-6 lg:col-start-7" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
              <PartnerForm />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="ds-container pb-20">
        <div className="max-w-2xl mx-auto text-center">
          <motion.p className="overline mb-3" {...fade}>questions?</motion.p>
          <motion.h3 className="text-xl font-bold mb-3" {...fade}>Still have questions?</motion.h3>
          <motion.p className="text-sm text-muted-foreground mb-4" {...fade}>
            Check our full <Link to="/faq" className="text-primary hover:underline">FAQ</Link> or email{" "}
            <a href="mailto:partner@ordrs.ai" className="text-primary hover:underline">partner@ordrs.ai</a>.
          </motion.p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
